package com.flowyride.data.account

import com.flowyride.core.api.AccountApi
import com.flowyride.core.api.ChangePasswordRequestDto
import com.flowyride.core.api.LoginRequestDto
import com.flowyride.core.api.LoginResponseDto
import com.flowyride.core.api.UserDto
import com.flowyride.core.api.toDomain
import com.flowyride.core.api.toDto
import com.flowyride.core.domain.account.AccountException
import com.flowyride.core.domain.account.AccountRepository
import com.flowyride.core.domain.account.RegistrationRequest
import com.flowyride.core.domain.account.SessionState
import com.flowyride.data.account.internal.accountCall
import com.flowyride.data.account.internal.ensureSuccess
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.flow.StateFlow

/**
 * @param client a client built with [withServerSession].
 * @param baseUrl the FlowyRide server, e.g. `https://flowyride.example.org`.
 */
class HttpAccountRepository(
    private val client: HttpClient,
    baseUrl: String,
    private val serverSession: ServerSession,
    private val useSessionCookie: Boolean = platformUsesSessionCookie,
) : AccountRepository {

    private val baseUrl = baseUrl.trimEnd('/')

    override val session: StateFlow<SessionState> = serverSession.state

    override suspend fun restoreSession() {
        if (!useSessionCookie && serverSession.token == null) {
            serverSession.signedOut()
            return
        }
        accountCall {
            val response = client.get(baseUrl + AccountApi.ME)
            if (response.status == HttpStatusCode.Unauthorized) {
                serverSession.signedOut()
            } else {
                response.ensureSuccess()
                serverSession.signedIn(response.body<UserDto>().toDomain(), token = null)
            }
        }
    }

    override suspend fun register(request: RegistrationRequest) {
        accountCall {
            client.post(baseUrl + AccountApi.REGISTER) {
                contentType(ContentType.Application.Json)
                setBody(request.toDto())
            }.ensureSuccess()
        }
    }

    override suspend fun signIn(login: String, password: String) = accountCall {
        val response = client.post(baseUrl + AccountApi.LOGIN) {
            contentType(ContentType.Application.Json)
            setBody(LoginRequestDto(login.trim(), password, cookieSession = useSessionCookie))
        }.ensureSuccess()
        val body = response.body<LoginResponseDto>()
        if (!useSessionCookie && body.token == null) throw AccountException.Invalid("Missing session token")
        serverSession.signedIn(body.user.toDomain(), body.token)
    }

    override suspend fun changePassword(currentPassword: String, newPassword: String) {
        accountCall {
            client.post(baseUrl + AccountApi.PASSWORD) {
                contentType(ContentType.Application.Json)
                setBody(ChangePasswordRequestDto(currentPassword, newPassword))
            }.ensureSuccess()
        }
    }

    override suspend fun signOut() {
        // Signed out locally even when the server cannot be reached.
        runCatching { client.post(baseUrl + AccountApi.LOGOUT) }
        serverSession.signedOut()
    }
}
